import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./DashboardPanelSwitcher.css";
import {
  getAdminAccess,
  getSupervisorAccess,
  getSemanticAdminAccess,
  USER_ACCESS_CHANGED_EVENT,
} from "../../../utils/userUtils";
import { performAppLogout } from "../../../utils/authLogout";

function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconAdmin() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function IconDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function IconSemanticQuery() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 8h8" />
      <path d="M8 12h5" />
    </svg>
  );
}

function IconSemanticThinking() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3a4 4 0 0 0-4 4v1a4 4 0 0 0 0 8v1a4 4 0 0 0 7 2" />
      <path d="M15 3a4 4 0 0 1 4 4v1a4 4 0 0 1 0 8v1a4 4 0 0 1-7 2" />
      <path d="M12 5v14" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const LINKS = [
  { path: "/departments", label: "Departments", desc: "Business department workspaces", Icon: IconDashboard },
  { path: "/chat", label: "Chat Panel", desc: "Back to AI assistant", Icon: IconChat },
  { path: "/admin", label: "Admin Panel", desc: "Dashboard & analytics", Icon: IconAdmin },
  { path: "/dashboard", label: "Dashboard", desc: "Sales, brand, diagnosis & division performance", Icon: IconDashboard },
  { path: "/semantic-query-engine", label: "Query Engine", desc: "Semantic model query workspace", Icon: IconSemanticQuery },
];

const ACCESS_STORAGE_KEYS = ["hasAdminAccess", "hasSupervisorAccess", "hasSemanticAdminAccess"];

function pathIsActive(pathname, path) {
  if (path === "/departments") {
    return pathname === "/departments";
  }
  if (path === "/chat") {
    return pathname === "/chat" || pathname.startsWith("/chat/");
  }
  if (path === "/admin") {
    return pathname === "/admin" || pathname.startsWith("/admin/");
  }
  if (path === "/dashboard") {
    return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
  }
  if (path === "/ai-sales-diagnosis") {
    return pathname === "/ai-sales-diagnosis" || pathname.startsWith("/ai-sales-diagnosis/");
  }
  if (path === "/semantic-query-engine") {
    return pathname === "/semantic-query-engine" || pathname.startsWith("/semantic-query-engine/");
  }
  return pathname === path;
}

/** Hide redundant self-links on standalone dashboard pages (default variant only). */
function visibleLinksForPathname(pathname, links) {
  return links.filter((link) => {
    if (link.path === "/brand-dashboard" && pathname === "/brand-dashboard") return false;
    if (link.path === "/sales-dashboard" && pathname === "/sales-dashboard") return false;
    if (link.path === "/departments" && pathname === "/departments") return false;
    if (link.path === "/ai-sales-diagnosis" && (pathname === "/ai-sales-diagnosis" || pathname.startsWith("/ai-sales-diagnosis/"))) return false;
    if (link.path === "/dashboard" && (pathname === "/dashboard" || pathname.startsWith("/dashboard/"))) return false;
    if (link.path === "/semantic-query-engine" && (pathname === "/semantic-query-engine" || pathname.startsWith("/semantic-query-engine/"))) return false;
    return true;
  });
}

function itemClassName(variant, pathname, path, disabled) {
  const active = pathIsActive(pathname, path);
  if (variant === "admin") {
    return `admin-panel-switcher-item${active ? " admin-panel-switcher-item-active" : ""}`;
  }
  if (variant === "chat") {
    return `panel-switcher-item${active ? " panel-switcher-item-active" : ""}${disabled ? " panel-switcher-item-disabled" : ""}`;
  }
  return `panel-switcher-item ${disabled ? "panel-switcher-item-disabled" : ""} ${active ? "panel-switcher-item-current" : ""}`.trim();
}

/**
 * @param {"default"|"chat"|"admin"} variant — "chat" / "admin" match ChatSection and AdminDashboard headers.
 * @param {"up"|"down"} direction — "up" opens above trigger, "down" opens below. Default variant defaults to "up", others to "down".
 */
export default function DashboardPanelSwitcher({ variant = "default", direction }) {
  const resolvedDirection = direction || (variant === "default" ? "up" : "down");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [hasAdminAccess, setHasAdminAccess] = useState(() => getAdminAccess());
  const [hasSupervisorAccess, setHasSupervisorAccess] = useState(() => getSupervisorAccess());
  const [hasSemanticAdminAccess, setHasSemanticAdminAccess] = useState(() => getSemanticAdminAccess());

  const refreshAccessFlags = useCallback(() => {
    setHasAdminAccess(getAdminAccess());
    setHasSupervisorAccess(getSupervisorAccess());
    setHasSemanticAdminAccess(getSemanticAdminAccess());
  }, []);

  useEffect(() => {
    refreshAccessFlags();
  }, [refreshAccessFlags]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!event.key || ACCESS_STORAGE_KEYS.includes(event.key)) refreshAccessFlags();
    };

    window.addEventListener(USER_ACCESS_CHANGED_EVENT, refreshAccessFlags);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(USER_ACCESS_CHANGED_EVENT, refreshAccessFlags);
      window.removeEventListener("storage", handleStorage);
    };
  }, [refreshAccessFlags]);

  const resolvedLinks = useMemo(() => {
    const gated = LINKS.filter((link) => {
      if (link.path === "/admin") return hasAdminAccess;
      if (link.path === "/dashboard") return hasSupervisorAccess;
      if (link.path === "/semantic-query-engine") return hasSemanticAdminAccess;
      return true;
    });
    if (variant === "chat") return gated.filter((l) => l.path !== "/chat");
    if (variant === "admin") return gated.filter((l) => l.path !== "/admin");
    return visibleLinksForPathname(pathname, gated);
  }, [variant, pathname, hasAdminAccess, hasSupervisorAccess, hasSemanticAdminAccess]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const rootClass =
    variant === "chat"
      ? "panel-switcher-wrapper dashboard-panel-switcher-chat"
      : variant === "admin"
        ? "admin-panel-switcher-wrapper"
        : `panel-switcher-wrapper dashboard-panel-switcher${resolvedDirection === "down" ? " panel-switcher-dropdown-dir" : ""}`;

  const triggerClass = variant === "admin" ? "nav-to-chat-btn" : "nav-to-admin-btn-chat";
  const dropdownClass = variant === "admin" ? "admin-panel-switcher-dropdown" : "panel-switcher-dropdown";

  return (
    <div
      className={rootClass}
      ref={wrapperRef}
      style={variant === "admin" ? undefined : { position: "relative" }}
    >
      <button
        type="button"
        className={triggerClass}
        onClick={(e) => {
          e.stopPropagation();
          refreshAccessFlags();
          setOpen((prev) => !prev);
        }}
        title="Switch Panel"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
        <span>Switch Panel</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points={resolvedDirection === "up" ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
        </svg>
      </button>

      {open && (
        <div className={dropdownClass} onClick={(e) => e.stopPropagation()}>
          {resolvedLinks.map(({ path, label, desc, Icon, disabled }) => (
            <button
              key={path}
              type="button"
              disabled={Boolean(disabled)}
              className={itemClassName(variant, pathname, path, disabled)}
              onClick={() => {
                if (disabled) return;
                setOpen(false);
                navigate(path);
              }}
            >
              <Icon />
              <div className={variant === "admin" ? "admin-panel-switcher-item-info" : "panel-switcher-item-info"}>
                <span className={variant === "admin" ? "admin-panel-switcher-item-label" : "panel-switcher-item-label"}>{label}</span>
                <span className={variant === "admin" ? "admin-panel-switcher-item-desc" : "panel-switcher-item-desc"}>{desc}</span>
              </div>
              <ChevronRight />
            </button>
          ))}
          <hr className="panel-switcher-divider" />
          <button
            type="button"
            className={variant === "admin" ? "admin-panel-switcher-item admin-panel-switcher-item-logout" : "panel-switcher-item panel-switcher-item-logout"}
            onClick={() => {
              setOpen(false);
              performAppLogout();
            }}
          >
            <IconLogout />
            <div className={variant === "admin" ? "admin-panel-switcher-item-info" : "panel-switcher-item-info"}>
              <span className={variant === "admin" ? "admin-panel-switcher-item-label" : "panel-switcher-item-label"}>Logout</span>
              <span className={variant === "admin" ? "admin-panel-switcher-item-desc" : "panel-switcher-item-desc"}>Sign out of your account</span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
